import React, { useEffect, useState } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import SocialIcon from '../../UI/SocialIcon/SocialIcon';
import TextLink from '../../UI/TextLink/TextLink';
import BackToTop from '../../elements/BackToTop/BackToTop';
import generateCssClassName from '../../../utils/generateCssClassName/generateCssClassName';
import { isKiosk } from '../../../utils/isKiosk';

import {
  getActiveGroups,
  getCategoryList,
  removeCookies,
} from '../../../utils/oneTrustHelper';

import { FooterI } from '../../../ContentfulTypes';
import { Logo } from '../../../types';
import { ImageLoadingType } from '../../UI/Image/Image';
import {
  FooterWrapper,
  Container,
  LinkList,
  StyledImage,
  LogoLink,
  Navigation,
  SocialContainer,
  A11yContainer,
  Spacer,
  CookieSettingsButton,
} from './styles';
import shouldShowCookieSettingsButton from './shouldShowCookieSettingsButton';

interface Props {
  data: FooterI;
  preferredLocale: string;
  province?: string;
  footerWrapper?: boolean;
}

const Footer = ({ data, preferredLocale, province = '', footerWrapper = true }: Props) => {
  const isKioskMode = isKiosk();
  const intl = useIntl();
  const [showCookieButton, setShowCookieButton] = useState(false);
  const getQCGeolocation = () => {
    if ((window as any)?.OneTrust && (window as any)?.OneTrust?.getGeolocationData) {
      const geo = (window as any)?.OneTrust?.getGeolocationData();
      if (geo) {
        setShowCookieButton(shouldShowCookieSettingsButton(geo));
      }
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      getQCGeolocation();
      // add event listener to OTSDK wrapper being called
      // dictates when the OneTrust api is available
      window.addEventListener('oneTrustCategoriesChanged', () => {
        getQCGeolocation();
        const activeGroups = getActiveGroups();
        const categoryGroups = getCategoryList();
        removeCookies(categoryGroups, activeGroups);
      });
    }
    return () => {
      window.removeEventListener('oneTrustCategoriesChanged', getQCGeolocation);
    };
  });

  const handleOneTrustButton = () => {
    // OneTrust is from a third party script
    // eslint-disable-next-line no-undef
    OneTrust.ToggleInfoDisplay();
  };

  return (
    <FooterWrapper $footerWrapper={footerWrapper}>
      <Container>
        <LogoLink to={`/${preferredLocale}`}>
          <StyledImage image={data.logoDam} loading={ImageLoadingType.Lazy} />
        </LogoLink>
        <Navigation data-testid="footer-navigation">
          <ul>
            {data.footerLinks.map(
              (link) => !(preferredLocale === 'fr' && link.ctaUrl === '/blog') && (
              <LinkList
                key={link.id}
                className={`${generateCssClassName(
                  link.ctaHideOption as Array<string>,
                  isKioskMode,
                )} ${link.ctaUrl.replace('/', '')}`}
              >
                <TextLink
                  linkText={link.ctaLabel.ctaLabel}
                  url={link.ctaUrl}
                  route={link.ctaTarget}
                  locale={preferredLocale}
                  data-testid="text-link-id"
                />
              </LinkList>
              ),
            )}
            {showCookieButton && (
              <LinkList>
                <CookieSettingsButton
                  id="ot-sdk-btn"
                  type="button"
                  // eslint-disable-next-line no-undef
                  onClick={handleOneTrustButton}
                >
                  {intl.formatMessage({ id: 'CookieSettings' })}
                </CookieSettingsButton>
              </LinkList>
            )}
          </ul>
        </Navigation>
        {/* We do not have social media for QC or French */}
        {/* On the fr site display the accessibility icon in social icon space */}
        {preferredLocale === 'fr' || province === 'QC' ? (
          <A11yContainer locale={preferredLocale}>
            <SocialIcon
              socialLink={data.accessibilityIcon.ctaUrl}
              image={data.accessibilityIcon.ctaIconDam as Logo}
            />
          </A11yContainer>
        ) : (
          <>
            <SocialContainer>
              {data.footerBadges.map((icon) => (
                <SocialIcon
                  key={icon.id}
                  socialLink={icon.ctaUrl}
                  image={icon.ctaIconDam as Logo}
                  className={generateCssClassName(
                    icon.ctaHideOption as Array<string>,
                    isKioskMode,
                  )}
                />
              ))}
            </SocialContainer>
            <A11yContainer isKioskMode={isKioskMode}>
              <SocialIcon
                socialLink={data.accessibilityIcon.ctaUrl}
                image={data.accessibilityIcon.ctaIconDam as Logo}
              />
            </A11yContainer>
          </>
        )}
      </Container>
      <BackToTop />
    </FooterWrapper>
  );
};

export const FooterSpacer = (props) => <Spacer {...props} />;

export default injectIntl(Footer);
